// src/services/pftExtractionStatus.js
//
// Which section each uploaded PFT is in on the admin upload page, and the "Extract next
// batch" choices. Firebase-free and tested (pftExtractionStatus.test.js). The server
// (functions/pftBatch.js) makes the real choice of files; this only shows the state.
//
// Sections:
//   Not extracted yet  uploaded, never extracted (or left by a run that stopped)
//   Extracting now     in the run that is going
//   Extracted          done (incl. ones where no PFT was found: tests = 0, and identical
//                      copies of a file already extracted: status "duplicate", not read)
//   Failed             a reading failed twice; tried again on the next run

export const BATCH_SIZES = [100, 200, 300, 400, 500];
export const EST_COST_PER_PDF_USD = 0.25; // keep in step with functions/pftBatch.js
const STALE_RUN_MS = 24 * 60 * 60 * 1000; // ditto

const toMillis = (v) => {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (typeof v.toMillis === "function") return v.toMillis();
  const t = Date.parse(v);
  return Number.isNaN(t) ? 0 : t;
};

/** Is this run still going (queued or running, and not stale)? Same rule as the server. */
export const isRunActive = (run, now = Date.now()) =>
  !!run && ["queued", "running"].includes(run.status) && now - toMillis(run.createdAt) < STALE_RUN_MS;

/** The newest run, or null. */
export const latestRun = (runs = []) =>
  runs.slice().sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))[0] || null;

/** How far along a run is: { finished, total, pct }. */
export const runProgress = (run) => {
  if (!run) return { finished: 0, total: 0, pct: 0 };
  const total = Number(run.total) || 0;
  const finished = Math.min(
    total,
    ["done", "failed", "skipped", "notRead", "duplicates"].reduce((n, k) => n + (Number(run[k]) || 0), 0),
  );
  return { finished, total, pct: total ? Math.round((finished / total) * 100) : 0 };
};

/**
 * Sort the uploaded files into sections.
 *   storedNames  object file names in the site's upload folder (from the bucket list)
 *   folderPath   "uploads/<tenant>/<folder>/"
 *   fileStates   pft_files docs ({ file, status, runId, tests, flaggedTests, error })
 *   activeRunId  the run that is going, or null
 * Returns { notExtracted, extracting, extracted, failed }: each a list of
 *   { name (stored), state (the pft_files doc or null) }.
 */
export const sortIntoSections = (storedNames = [], folderPath, fileStates = [], activeRunId = null) => {
  const byFile = new Map(fileStates.filter((s) => s && s.file).map((s) => [s.file, s]));
  const out = { notExtracted: [], extracting: [], extracted: [], failed: [] };
  for (const name of storedNames) {
    const state = byFile.get(`${folderPath}${name}`) || null;
    const status = state && state.status;
    if (status === "extracted" || status === "duplicate") out.extracted.push({ name, state });
    else if (status === "failed") out.failed.push({ name, state });
    else if (status === "extracting" && activeRunId && state.runId === activeRunId) out.extracting.push({ name, state });
    else out.notExtracted.push({ name, state }); // never tried, or its run stopped
  }
  return out;
};

/** How many files the next run would take at this batch size. */
export const nextBatchCount = (sections, size) =>
  Math.min(size, sections.notExtracted.length + sections.failed.length);

export const estimateUsd = (n) => Math.round(n * EST_COST_PER_PDF_USD);
